import cn from 'classnames';
import { noop } from 'lodash';
import { type MemoExoticComponent, type MouseEventHandler, memo, useContext } from 'react';
import type { To } from 'react-router-dom';

import { Tooltip } from '../../../general/tooltip/Tooltip';
import { Link } from '../../link/Link';
import { MenuContext } from '../Menu.context';
import { MenuItem, type MenuItemProps } from './MenuItem';
import { containerStyle } from './MenuItem.styles';

export type MenuItemLinkProps = MenuItemProps & {
  href: To;
  isExternal?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  className?: string;
};

export const MenuItemLinkBase = ({
  label,
  href,
  onClick = noop,
  isExternal = false,
  isSelected = false,
  isDisabled = false,
  className = null,
  ...props
}: MenuItemLinkProps) => {
  const { isCollapsed } = useContext(MenuContext);

  const itemContent = (
    <li className={cn(className)}>
      <Link
        to={!isExternal ? href : undefined}
        href={isExternal ? (href as string) : undefined}
        isDisabled={!href || isDisabled}
        isExternal={isExternal}
        className={cn({ isSelected, isCollapsed, isDisabled })}
        css={containerStyle}
        onClick={onClick}
      >
        <MenuItem {...props} label={label} isSelected={isSelected} isDisabled={isDisabled} />
      </Link>
    </li>
  );

  return isCollapsed ? (
    <Tooltip content={label} placement="right">
      {itemContent}
    </Tooltip>
  ) : (
    itemContent
  );
};

MenuItemLinkBase.displayName = 'MenuItemLink';

export const MenuItemLink: MemoExoticComponent<typeof MenuItemLinkBase> & {
  Tag?: typeof MenuItem.Tag;
} = memo(MenuItemLinkBase);

MenuItemLink.Tag = MenuItem.Tag;
